import React from 'react';
import { Clock, Activity, AlertTriangle } from 'lucide-react';

const CashDurationCalculator = ({ data, onChange, calculations, formatCurrency }) => {
  const formatInput = (val) => {
    if (!val) return '';
    return String(val);
  };

  const getRunwayStatus = () => {
    const months = calculations?.cashRunwayMonths;
    if (months >= 12) return { color: 'green', text: 'Healthy', icon: Activity };
    if (months >= 6) return { color: 'blue', text: 'Moderate', icon: Clock };
    if (months >= 3) return { color: 'amber', text: 'Caution', icon: AlertTriangle };
    return { color: 'red', text: 'Critical', icon: AlertTriangle };
  };

  const runwayStatus = getRunwayStatus();
  const StatusIcon = runwayStatus?.icon;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-10 h-10 rounded-full bg-${runwayStatus?.color}-100 flex items-center justify-center`}>
          <StatusIcon className={`w-5 h-5 text-${runwayStatus?.color}-600`} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">Cash Duration Calculator</h3>
          <p className="text-xs text-slate-600">Runway analysis & scenario modeling</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Current Cash Position
            </label>
            <input
              type="text"
              value={formatInput(data?.currentCash)}
              onChange={(e) => onChange(null, 'currentCash', e?.target?.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
              placeholder="Enter current cash"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Monthly Revenue
            </label>
            <input
              type="text"
              value={formatInput(data?.monthlyRevenue)}
              onChange={(e) => onChange(null, 'monthlyRevenue', e?.target?.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
              placeholder="Enter monthly revenue"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Revenue Growth Rate (%)
            </label>
            <input
              type="number"
              value={data?.revenueGrowthRate}
              onChange={(e) => onChange(null, 'revenueGrowthRate', e?.target?.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
              min="0"
              max="100"
              step="0.5"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Monthly Operating Costs
            </label>
            <input
              type="text"
              value={formatInput(data?.monthlyOperatingCosts)}
              onChange={(e) => onChange(null, 'monthlyOperatingCosts', e?.target?.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
              placeholder="Fixed operating expenses"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Staff Costs (Monthly)
            </label>
            <input
              type="text"
              value={formatInput(data?.staffCosts)}
              onChange={(e) => onChange(null, 'staffCosts', e?.target?.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
              placeholder="Total payroll"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Overhead Costs (Monthly)
            </label>
            <input
              type="text"
              value={formatInput(data?.overheadCosts)}
              onChange={(e) => onChange(null, 'overheadCosts', e?.target?.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
              placeholder="Rent, utilities, etc"
            />
          </div>

          {/* Runway Analysis Card */}
          <div className="mt-6 p-5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
            <h4 className="text-sm font-bold text-slate-900 mb-4">Runway Analysis</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Cash Runway</span>
                <strong className={`text-lg text-${runwayStatus?.color}-600`}>
                  {calculations?.cashRunwayMonths?.toFixed(1)} months
                </strong>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Monthly Burn</span>
                <strong className="text-base text-slate-900">
                  {formatCurrency(calculations?.totalMonthlyBurn)}
                </strong>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Break Even Point</span>
                <strong className="text-base text-slate-900">
                  {formatCurrency(calculations?.breakEvenPoint)}
                </strong>
              </div>

              <div className={`mt-4 p-3 bg-${runwayStatus?.color}-50 rounded-lg border border-${runwayStatus?.color}-200`}>
                <div className="flex items-center gap-2">
                  <StatusIcon className={`w-4 h-4 text-${runwayStatus?.color}-600`} />
                  <span className={`text-sm font-medium text-${runwayStatus?.color}-900`}>
                    Status: {runwayStatus?.text}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Critical Cash Level Warning */}
      {calculations?.cashRunwayMonths < 3 && (
        <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-red-900 mb-1">Critical Cash Position</p>
              <p className="text-xs text-red-700">
                Your cash runway is below 3 months. Consider immediate actions: reduce operating costs, 
                secure additional funding, or accelerate revenue growth strategies.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashDurationCalculator;